import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Field, Input, Card, EmptyState, KeyValue } from '../components/ui';
import { Can } from '../components/Can';

type C = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * UK Construction Industry Scheme.
 *
 * Lotus builds through Ivaro and engages subcontractors, which makes it a CIS
 * contractor: verify each subcontractor, withhold 20% or 30% from the LABOUR element
 * only, and file a CIS300 for every tax month — the 6th to the 5th, due by the 19th,
 * including nil returns.
 */
export default function CisPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [open, setOpen] = useState<C | null>(null);
  const [submitting, setSubmitting] = useState<C | null>(null);
  const [paying, setPaying] = useState<C | null>(null);
  const [verifying, setVerifying] = useState<C | null>(null);
  const [submitForm, setSubmitForm] = useState({ hmrcReference: '', submittedOn: today() });
  const [payForm, setPayForm] = useState({ paidOn: today(), bankAccountId: '' });
  const [verifyForm, setVerifyForm] = useState({ verificationNumber: '', cisStatus: 'NET_20' });

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<C[]>('/entities')).data });
  const target = entityId || entities.data?.find((e) => e.region === 'UK')?.id || '';
  const banks = useQuery({ queryKey: ['bank-accounts', target], queryFn: async () => (await api.get<C[]>('/bank-accounts', { entityId: target })).data, enabled: !!target });
  const returns = useQuery({ queryKey: ['cis-returns', entityId], queryFn: async () => (await api.get<C[]>('/cis/returns', { entityId: entityId || undefined })).data });
  const detail = useQuery({ queryKey: ['cis-return', open?.id], queryFn: async () => (await api.get<C>(`/cis/returns/${open!.id}`)).data, enabled: !!open });
  const vendors = useQuery({ queryKey: ['vendors-cis'], queryFn: async () => (await api.get<C[]>('/vendors', { perPage: 200 })).data });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['cis-returns'] }); void qc.invalidateQueries({ queryKey: ['vendors-cis'] }); if (open) void qc.invalidateQueries({ queryKey: ['cis-return', open.id] }); };

  const generate = useMutation({ mutationFn: async () => api.post('/cis/returns/generate', { entityId: target }), onSuccess: refresh });
  const submit = useMutation({ mutationFn: async () => api.post(`/cis/returns/${submitting!.id}/submit`, { ...submitForm, nilReturn: submitting!.totalPaymentsMinor === 0 }), onSuccess: () => { setSubmitting(null); refresh(); } });
  const payHmrc = useMutation({ mutationFn: async () => api.post(`/cis/returns/${paying!.id}/pay`, { paidOn: payForm.paidOn, bankAccountId: payForm.bankAccountId || undefined }), onSuccess: () => { setPaying(null); refresh(); } });
  const verify = useMutation({ mutationFn: async () => api.post(`/cis/subcontractors/${verifying!.id}/verify`, verifyForm), onSuccess: () => { setVerifying(null); refresh(); } });

  const d = detail.data;
  const subbies = vendors.data?.filter((v) => v.cisStatus && v.cisStatus !== 'NOT_APPLICABLE') ?? [];

  return (
    <div>
      <PageHeader
        title="CIS — subcontractors"
        subtitle="Deductions are withheld from labour only, never materials, and paid over to HMRC. A tax month runs from the 6th to the 5th and its return is due by the 19th — including a nil return when nobody was paid."
        actions={<Can do="cis.update"><div className="flex items-center gap-2">
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}><option value="">All companies</option>{entities.data?.filter((e) => e.region === 'UK').map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
          <Button icon="FilePlus" onClick={() => generate.mutate()} disabled={generate.isPending || !target}>Build this month's return</Button>
        </div></Can>}
      />

      {generate.isError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(generate.error as Error).message}</p>}

      {!returns.data?.length ? (
        <EmptyState icon="HardHat" title="No CIS returns yet" body="Build the return for the last completed tax month; it sweeps in every deduction from approved bills." />
      ) : (
        <div className="surface mb-6 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/70 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-400">
              <th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Tax month</th><th className="px-4 py-2.5">Due</th>
              <th className="px-4 py-2.5 text-right">Payments</th><th className="px-4 py-2.5 text-right">Materials</th><th className="px-4 py-2.5 text-right">Deducted</th>
              <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {returns.data.map((c) => (
                <tr key={c.id} className="cursor-pointer border-b border-ink-50 last:border-0 hover:bg-ink-50/50" onClick={() => setOpen(c)}>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-ink-600">{c.reference}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap"><DateText value={c.taxMonthStart} /> — <DateText value={c.taxMonthEnd} /></td>
                  <td className="px-4 py-2.5 whitespace-nowrap"><span className={c.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={c.dueDate} /></span></td>
                  <td className="px-4 py-2.5 text-right"><Money minor={c.totalPaymentsMinor} currency={c.currency} /></td>
                  <td className="px-4 py-2.5 text-right text-ink-500"><Money minor={c.totalMaterialsMinor} currency={c.currency} /></td>
                  <td className="px-4 py-2.5 text-right font-medium text-amber-700"><Money minor={c.totalDeductionsMinor} currency={c.currency} /></td>
                  <td className="px-4 py-2.5"><Badge value={c.nilReturn && c.status === 'DRAFT' ? 'NIL' : c.status} tone={c.status === 'SUBMITTED' ? 'good' : 'neutral'} /></td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {c.status === 'DRAFT' && <Can do="cis.submit"><Button size="sm" onClick={() => { setSubmitForm({ hmrcReference: '', submittedOn: today() }); setSubmitting(c); }}>Record submission</Button></Can>}
                      {c.totalDeductionsMinor > 0 && <Can do="cis.update"><Button size="sm" variant="secondary" onClick={() => { setPayForm({ paidOn: today(), bankAccountId: '' }); setPaying(c); }}>Pay HMRC</Button></Can>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Card title="Subcontractors" subtitle="Verification sets the rate. An unverified subcontractor is deducted at 30% — under-deducting is the contractor's liability.">
        {!subbies.length ? <p className="text-[13px] text-ink-500">No subcontractors in the scheme yet.</p> : (
          <table className="w-full text-[13px]">
            <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400"><th className="py-1.5">Name</th><th className="py-1.5">UTR</th><th className="py-1.5">Status</th><th className="py-1.5">Verification</th><th /></tr></thead>
            <tbody>
              {subbies.map((v) => (
                <tr key={v.id} className="border-b border-ink-50 last:border-0">
                  <td className="py-2 text-ink-800">{v.name}</td>
                  <td className="py-2 font-mono text-[12px] text-ink-500">{v.taxNumber ?? '—'}</td>
                  <td className="py-2"><Badge value={v.cisStatus === 'GROSS' ? 'Gross · 0%' : v.cisStatus === 'NET_20' ? 'Verified · 20%' : 'Unmatched · 30%'} tone={v.cisStatus === 'GROSS' ? 'good' : v.cisStatus === 'NET_20' ? 'neutral' : 'warning'} /></td>
                  <td className="py-2 font-mono text-[12px] text-ink-500">{v.cisVerificationNumber ?? <span className="text-red-600">not verified</span>}</td>
                  <td className="py-2 text-right"><Can do="cis.update"><Button size="sm" variant="ghost" onClick={() => { setVerifyForm({ verificationNumber: v.cisVerificationNumber ?? '', cisStatus: v.cisStatus ?? 'NET_20' }); setVerifying(v); }}>Record verification</Button></Can></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Detail */}
      <Modal open={!!open} onClose={() => setOpen(null)} size="lg" title={d?.reference ?? open?.reference ?? 'CIS return'}>
        {d && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><Badge value={d.status} /><span className="text-xs text-ink-400">{d.taxMonthStart} to {d.taxMonthEnd}</span></div>
            <KeyValue items={[
              { label: 'Total payments', value: <Money minor={d.totalPaymentsMinor} currency={d.currency} /> },
              { label: 'Cost of materials', value: <Money minor={d.totalMaterialsMinor} currency={d.currency} /> },
              { label: 'Total deducted', value: <Money minor={d.totalDeductionsMinor} currency={d.currency} /> },
              { label: 'Due to HMRC by', value: <DateText value={d.dueDate} /> },
            ]} />

            {d.nilReturn && <p className="rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-2.5 text-[12.5px] text-ink-700">Nil return — no subcontractors were paid in this tax month. HMRC still has to be told.</p>}

            {!!d.subcontractors?.length && (
              <Card title="By subcontractor" subtitle="One line per subcontractor per month, the way HMRC wants it">
                <table className="w-full text-[13px]">
                  <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400"><th className="py-1.5">Subcontractor</th><th className="py-1.5">UTR</th><th className="py-1.5 text-right">Gross</th><th className="py-1.5 text-right">Materials</th><th className="py-1.5 text-right">Labour</th><th className="py-1.5 text-right">Deducted</th></tr></thead>
                  <tbody>
                    {d.subcontractors.map((s: C) => (
                      <tr key={s.vendorId} className="border-b border-ink-50 last:border-0">
                        <td className="py-1.5 text-ink-800">{s.vendorName}<div className="text-[11px] text-ink-400">{s.cisStatus} {s.verificationNumber ? `· ${s.verificationNumber}` : '· not verified'}</div></td>
                        <td className="py-1.5 font-mono text-[11.5px] text-ink-500">{s.utr ?? '—'}</td>
                        <td className="py-1.5 text-right"><Money minor={s.grossMinor} currency={d.currency} /></td>
                        <td className="py-1.5 text-right text-ink-500"><Money minor={s.materialsMinor} currency={d.currency} /></td>
                        <td className="py-1.5 text-right"><Money minor={s.labourMinor} currency={d.currency} /></td>
                        <td className="py-1.5 text-right font-medium text-amber-700"><Money minor={s.deductionMinor} currency={d.currency} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            <p className="rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-2.5 text-[12px] leading-relaxed text-ink-600">
              <strong className="font-medium text-ink-800">Not implemented — transmission and verification.</strong> The CIS300 is prepared here, not filed, and subcontractor verification is recorded rather than requested: both need a recognised channel and Government Gateway credentials.
            </p>
            {d.notes && <p className="whitespace-pre-wrap text-xs text-ink-500">{d.notes}</p>}
          </div>
        )}
      </Modal>

      {/* Submit */}
      <Modal open={!!submitting} onClose={() => setSubmitting(null)} title="Record the CIS300 submission" footer={<>
        <Button variant="secondary" onClick={() => setSubmitting(null)}>Cancel</Button>
        <Button onClick={() => submit.mutate()} disabled={submit.isPending}>Record as submitted</Button>
      </>}>
        <p className="mb-3 text-[13px] text-ink-600">{submitting?.totalPaymentsMinor === 0 ? 'This is a nil return — no subcontractors were paid, and HMRC still needs telling.' : 'Enter the reference HMRC returned after you submitted the CIS300.'}</p>
        <div className="space-y-3">
          <Field label="HMRC reference"><Input value={submitForm.hmrcReference} onChange={(e) => setSubmitForm({ ...submitForm, hmrcReference: e.target.value })} className="w-full" /></Field>
          <Field label="Submitted on"><Input type="date" value={submitForm.submittedOn} onChange={(e) => setSubmitForm({ ...submitForm, submittedOn: e.target.value })} className="w-full" /></Field>
        </div>
        {submit.isError && <p className="mt-2 text-xs text-red-600">{(submit.error as Error).message}</p>}
      </Modal>

      {/* Pay HMRC */}
      <Modal open={!!paying} onClose={() => setPaying(null)} title="Pay the deductions to HMRC" footer={<>
        <Button variant="secondary" onClick={() => setPaying(null)}>Cancel</Button>
        <Button onClick={() => payHmrc.mutate()} disabled={payHmrc.isPending}>Post payment</Button>
      </>}>
        {paying && (
          <div className="space-y-3">
            <p className="text-[13px] text-ink-600">Posts Dr CIS deductions payable / Cr Bank for <strong><Money minor={paying.totalDeductionsMinor} currency={paying.currency} /></strong> — the liability built up bill by bill, settled in one payment.</p>
            <Field label="Paid on"><Input type="date" value={payForm.paidOn} onChange={(e) => setPayForm({ ...payForm, paidOn: e.target.value })} className="w-full" /></Field>
            <Field label="From"><Select value={payForm.bankAccountId} onChange={(e) => setPayForm({ ...payForm, bankAccountId: e.target.value })} className="w-full"><option value="">Default bank</option>{banks.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
            {payHmrc.isError && <p className="text-xs text-red-600">{(payHmrc.error as Error).message}</p>}
          </div>
        )}
      </Modal>

      {/* Verify */}
      <Modal open={!!verifying} onClose={() => setVerifying(null)} title={`Verification — ${verifying?.name ?? ''}`} footer={<>
        <Button variant="secondary" onClick={() => setVerifying(null)}>Cancel</Button>
        <Button onClick={() => verify.mutate()} disabled={!verifyForm.verificationNumber || verify.isPending}>Record</Button>
      </>}>
        <p className="mb-3 text-[13px] leading-relaxed text-ink-600">Record what HMRC returned when you verified this subcontractor. This is what moves them off the 30% unmatched rate, so it is audited.</p>
        <div className="space-y-3">
          <Field label="Verification number"><Input value={verifyForm.verificationNumber} onChange={(e) => setVerifyForm({ ...verifyForm, verificationNumber: e.target.value })} placeholder="V1234567890" className="w-full" /></Field>
          <Field label="Result">
            <Select value={verifyForm.cisStatus} onChange={(e) => setVerifyForm({ ...verifyForm, cisStatus: e.target.value })} className="w-full">
              <option value="GROSS">Gross payment status — 0%</option>
              <option value="NET_20">Registered — 20%</option>
              <option value="NET_30">Not matched — 30%</option>
              <option value="NOT_APPLICABLE">Outside the scheme</option>
            </Select>
          </Field>
        </div>
        {verify.isError && <p className="mt-2 text-xs text-red-600">{(verify.error as Error).message}</p>}
      </Modal>
    </div>
  );
}
